// NPSWidget — D+14 in-product NPS (CustDev backlog, Plane #75).
// Небольшая недестуктивная карточка (не модалка) в углу экрана — не блокирует работу.
// Показывается один раз (api-gateway/nps.ts решает shouldShow: не раньше 14 дней после
// signup, не повторно после первого ответа). Детрактор (score<=6) заводит issue в Plane
// на бэкенде — фронтенд об этом не знает и не должен.
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { api } from '../api/client';

const SCORES = Array.from({ length: 11 }, (_, i) => i); // 0..10

export function NPSWidget(): JSX.Element | null {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [dismissed, setDismissed] = useState(false);
  const [selectedScore, setSelectedScore] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const statusQ = useQuery({
    queryKey: ['nps-status'],
    queryFn: async () => (await api.get<{ shouldShow: boolean }>('/nps/status')).data,
    staleTime: 5 * 60_000,
    retry: false,
  });

  const submitMut = useMutation({
    mutationFn: async () => {
      await api.post('/nps', { score: selectedScore, comment: comment || undefined });
    },
    onSuccess: () => {
      setSubmitted(true);
      void qc.invalidateQueries({ queryKey: ['nps-status'] });
    },
  });

  if (dismissed || !statusQ.data?.shouldShow) return null;

  return (
    <div className="fixed bottom-6 right-6 z-40 w-80 card p-4 shadow-lg" role="dialog" aria-label="NPS">
      <button
        type="button"
        className="absolute right-2 top-2 text-brand-muted hover:text-brand-ink"
        aria-label={t('nps.dismiss')}
        onClick={() => setDismissed(true)}
      >
        <X size={16} />
      </button>
      {submitted ? (
        <p className="text-sm font-medium py-2">{t('nps.thanks')}</p>
      ) : (
        <>
          <p className="text-sm font-semibold pr-5 mb-3">{t('nps.title')}</p>
          <div className="flex gap-1 justify-between mb-1">
            {SCORES.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setSelectedScore(s)}
                className={`w-6 h-7 text-xs rounded ${selectedScore === s ? 'bg-brand-primary text-white' : 'bg-slate-100 text-brand-ink hover:bg-slate-200'}`}
              >
                {s}
              </button>
            ))}
          </div>
          <div className="flex justify-between text-[10px] text-brand-muted mb-3">
            <span>{t('nps.notLikely')}</span>
            <span>{t('nps.veryLikely')}</span>
          </div>
          <textarea
            className="w-full border border-slate-200 rounded-lg p-2 text-sm mb-3"
            rows={2}
            placeholder={t('nps.commentPlaceholder')}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
          <button
            type="button"
            className="btn-primary w-full text-sm py-2"
            disabled={selectedScore === null || submitMut.isPending}
            onClick={() => submitMut.mutate()}
          >
            {t('nps.submit')}
          </button>
        </>
      )}
    </div>
  );
}
